// ── Hash-based SPA router ──

const routes = [];
let currentCleanup = null;
// Bumped by every resolve(). A page's teardown is kept only while the navigation that mounted
// the page is still the latest one — otherwise that page is already gone (see adoptCleanup()).
// Pages read it through getNavigationId() to stop a mount that a navigation superseded.
let navSeq = 0;

function addRoute(pattern, handler) {
  const paramNames = [];
  const regexStr = pattern.replace(/:(\w+)/g, (_, name) => {
    paramNames.push(name);
    return '([^/]+)';
  });
  routes.push({ regex: new RegExp('^' + regexStr + '$'), paramNames, handler });
}

function navigate(path) {
  // '/' is workspace-scoped and never project-gated — it always resolves to the
  // dashboard regardless of which project (if any) was previously selected (C1275).
  const target = '#' + path;
  // If we're already at the target route, a plain hash assignment is a
  // no-op — no 'hashchange' event fires, so the route never re-renders
  // (e.g. login page bouncing to '/' when the app is already sitting on
  // '/' post-redirect). Force a re-resolve in that case.
  if (window.location.hash === target) {
    resolve();
  } else {
    window.location.hash = target;
  }
}

// A throwing teardown must not stop the next page from mounting.
function runCleanup(cleanup) {
  try {
    cleanup();
  } catch (err) {
    console.error('[router] page cleanup failed', err);
  }
}

// A route handler returns its page's teardown: a function (sync page) or a thenable resolving
// to one (every `async` page). An async page's teardown is unknown until its promise settles.
// It is then stored if navigation `seq` is still the latest, and run at once if the route
// changed meanwhile — that page is no longer mounted, and the page that is keeps its own
// teardown. Anything other than a function means the page has no teardown.
function adoptCleanup(result, seq) {
  const adopt = (cleanup) => {
    if (typeof cleanup !== 'function') return;
    if (seq === navSeq) currentCleanup = cleanup;
    else runCleanup(cleanup);
  };
  if (result && typeof result.then === 'function') {
    Promise.resolve(result).then(adopt, (err) => console.error('[router] page failed to load', err));
  } else {
    adopt(result);
  }
}

function resolve() {
  const raw = window.location.hash.slice(1) || '/';
  const qIdx = raw.indexOf('?');
  const hash = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const seq = ++navSeq;

  // Cleared before it runs, so it runs exactly once even if it re-enters resolve().
  if (currentCleanup) {
    const cleanup = currentCleanup;
    currentCleanup = null;
    runCleanup(cleanup);
  }

  for (const route of routes) {
    const match = hash.match(route.regex);
    if (match) {
      const params = {};
      route.paramNames.forEach((name, i) => { params[name] = match[i + 1]; });
      adoptCleanup(route.handler(params), seq);
      return;
    }
  }

  navigate('/');
}

function start() {
  window.addEventListener('hashchange', resolve);
  resolve();
}

// The id of the latest navigation. An async page reads it before its first await; once it
// differs, the user navigated (to another page, or re-mounted this one) and the mount must stop.
function getNavigationId() {
  return navSeq;
}

function getCurrentPath() {
  return window.location.hash.slice(1) || '/';
}

function getQueryParams() {
  const raw = window.location.hash.slice(1) || '/';
  const qIdx = raw.indexOf('?');
  if (qIdx < 0) return new URLSearchParams();
  return new URLSearchParams(raw.slice(qIdx + 1));
}

function setQueryParams(params) {
  const raw = window.location.hash.slice(1) || '/';
  const qIdx = raw.indexOf('?');
  const path = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const qs = params.toString();
  const newHash = qs ? '#' + path + '?' + qs : '#' + path;
  history.replaceState(null, '', newHash);
}

export { addRoute, navigate, start, resolve, getNavigationId, getCurrentPath, getQueryParams, setQueryParams };
